"use client";

import { use, useState } from "react";
import { LuCheck, LuUserPlus as LuUserPlus2 } from "react-icons/lu";

import { waitForTxn } from "@/backend/api-helpers";
import { createFollowTx } from "@/backend/focus";
import { InfoTooltip } from "@/components/shared/info-tooltip";
import { Spinner } from "@/components/shared/spinner";
import { Button } from "@/components/ui/button";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { useToast } from "@/components/ui/use-toast";
import { UserContext } from "@/contexts/user-context";
import { useIsClient } from "@/hooks/use-is-client";
import { useUserFollowStatus } from "@/hooks-api/use-user-follow-status";
import { cn } from "@/utils/lib";

type FollowButtonProps = {
  publicKey: string;
  className?: string;
  wrapperClassName?: string;
  variant?: "link" | "outlined" | "icon-text" | "default";
};

export const FollowButton = ({
  publicKey,
  className = "",
  variant = "link",
  wrapperClassName = "",
}: FollowButtonProps) => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { currentUserPublicKey } = use(UserContext);
  const isClient = useIsClient();

  const {
    loadingUser,
    isUserFollowedByPublicKey,
    refetch,
    isUserFollowLoading,
    user,
  } = useUserFollowStatus(publicKey);

  if (publicKey === currentUserPublicKey || !isClient) {
    return null;
  }

  const onToggleFollow = async () => {
    if (!user || loading) return;
    setLoading(true);

    const payload = {
      FollowedPublicKeyBase58Check: publicKey,
      FollowerPublicKeyBase58Check: user?.publicKey,
      IsUnfollow: isUserFollowedByPublicKey,
      MinFeeRateNanosPerKB: 1000,
    };

    try {
      const data = await createFollowTx(payload);

      if (!data.submittedTransactionResponse?.TxnHashHex) {
        return Promise.reject(
          "Transaction was not constructed. Please try again.",
        );
      }

      await waitForTxn({
        transactionHash: data.submittedTransactionResponse?.TxnHashHex,
      });
      await refetch();
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `An error happened when making subscription`,
        details: createToastErrorDetails(e),
      });
    } finally {
      setLoading(false);
    }
  };

  const isFollowing = !loading && isUserFollowedByPublicKey;
  const isNotFollowing = !loading && !isUserFollowedByPublicKey;
  const isLoading = loading || isUserFollowLoading || loadingUser;

  const outlinedButton = (
    <InfoTooltip text={isUserFollowedByPublicKey ? "Unfollow" : "Follow"}>
      <Button
        className={cn("flex items-center gap-2 text-xs", className)}
        variant="outline"
        onClick={onToggleFollow} // Add onClick event to toggle following state
      >
        {isFollowing && <LuCheck className="text-sm" />}
        {isLoading ? <Spinner /> : isFollowing ? "Unfollow" : "Follow"}
      </Button>
    </InfoTooltip>
  );

  const primaryButton = (
    <InfoTooltip text={isUserFollowedByPublicKey ? "Unfollow" : "Follow"}>
      <Button
        variant="default"
        className="flex items-center gap-2 text-xs"
        onClick={onToggleFollow} // Add onClick event to toggle following state
      >
        {isFollowing && <LuCheck className="text-sm" />}
        {isLoading ? <Spinner /> : isFollowing ? "Unfollow" : "Follow"}
      </Button>
    </InfoTooltip>
  );

  const linkButton = (
    <Button
      className={cn(
        `
          ml-auto cursor-pointer border-none bg-transparent leading-6
          font-normal text-inherit no-underline outline-hidden
        `,
        className,
      )}
      onClick={onToggleFollow}
    >
      {isLoading
        ? "Loading..."
        : isUserFollowedByPublicKey
          ? "Following"
          : "Follow"}
    </Button>
  );

  const iconText = (
    <Button
      className={cn(
        `
          cursor-pointer gap-2 text-sm text-muted-foreground no-underline
          outline-hidden
          hover:text-primary hover:no-underline
        `,
        className,
      )}
      variant="link"
      onClick={onToggleFollow}
    >
      {isLoading && <Spinner />}
      {isNotFollowing && (
        <>
          <LuUserPlus2 className="text-base text-muted" />
          <span>Follow</span>
        </>
      )}
      {isFollowing && (
        <>
          <LuCheck className="text-sm text-muted" />
          <span>Unfollow</span>
        </>
      )}
    </Button>
  );

  const buttonsByVariant = {
    outlined: outlinedButton,
    link: linkButton,
    "icon-text": iconText,
    default: primaryButton,
  };

  return <div className={wrapperClassName}>{buttonsByVariant[variant]}</div>;
};
